<template>
  <div class="calendar-page" :class="{ 'admin-mode': mode === 'admin' }">
    <!-- ローディングモーダル -->
    <LoadingModal :visible="loading" />
    
    <!-- ヘッダー（管理モードでは簡略化） -->
    <div class="calendar-header" v-if="mode === 'user'">
      <div class="header-stats">
        <div class="ticket-info">
          <span class="ticket-icon">🎫</span>
          <span class="ticket-text">今月 {{ getTicketsForMonth(currentMonth) }}/4</span>
        </div>
        <div class="ticket-info">
          <span class="ticket-icon">🎫</span>
          <span class="ticket-text">来月 {{ getTicketsForMonth(nextMonth) }}/4</span>
        </div>
      </div>
      <div class="header-subtitle">
        月4枚の計画休が使えます。土日祝は先生のお休みのため、連休期間中は1回のみ提出可能です。
      </div>
    </div>

    <!-- 管理モード用ヘッダー -->
    <div class="calendar-header admin-header" v-if="mode === 'admin'">
      <h2>📅 公式休日カレンダー</h2>
      <p class="admin-subtitle">
        土日は自動的に休日扱いとなります。以下は土日以外の特別休日です。
      </p>
    </div>

    <!-- カレンダーコンテナ -->
    <div class="calendar-container">
      <!-- ユーザーモード：今月＋来月のカレンダー -->
      <template v-if="mode === 'user'">
        <!-- 今月のカレンダー -->
        <div class="month-calendar">
          <div class="month-header">
            <h2 class="month-title">{{ formatMonth(currentMonth) }}</h2>
          </div>
          <div class="calendar-grid">
            <!-- 曜日ヘッダー -->
            <div class="weekday-header">
              <div
                class="calendar-weekday"
                v-for="(wd, i) in weekdays"
                :key="wd"
                :class="{ sun: i === 0, sat: i === 6 }"
              >
                {{ wd }}
              </div>
            </div>
            
            <!-- 日付セル -->
            <div class="calendar-week" v-for="(week, weekIndex) in currentMonthWeeks" :key="`current-${weekIndex}`">
              <div 
                class="calendar-day" 
                v-for="day in week" 
                :key="`current-${day.date}`"
                :class="getDayClasses(day)"
                :title="getDayTitle(day)"
                :aria-label="getDayTitle(day)"
                @click="handleDayClick(day)"
              >
                <div 
                  class="day-number"
                  :class="{
                    sun: day.isSunday,
                    sat: day.isSaturday
                  }"
                >
                  {{ day.dayNumber }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 来月のカレンダー -->
        <div class="month-calendar">
          <div class="month-header">
            <h2 class="month-title">{{ formatMonth(nextMonth) }}</h2>
          </div>
          <div class="calendar-grid">
            <!-- 曜日ヘッダー -->
            <div class="weekday-header">
              <div
                class="calendar-weekday"
                v-for="(wd, i) in weekdays"
                :key="wd"
                :class="{ sun: i === 0, sat: i === 6 }"
              >
                {{ wd }}
              </div>
            </div>
            
            <!-- 日付セル -->
            <div class="calendar-week" v-for="(week, weekIndex) in nextMonthWeeks" :key="`next-${weekIndex}`">
              <div 
                class="calendar-day" 
                v-for="day in week" 
                :key="`next-${day.date}`"
                :class="getDayClasses(day)"
                :title="getDayTitle(day)"
                :aria-label="getDayTitle(day)"
                @click="handleDayClick(day)"
              >
                <div 
                  class="day-number"
                  :class="{
                    sun: day.isSunday,
                    sat: day.isSaturday
                  }"
                >
                  {{ day.dayNumber }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- 管理モード：選択した年の1〜12月のカレンダー -->
      <template v-else-if="mode === 'admin'">
        <div class="yearly-calendar">
          <div
            v-for="month in 12"
            :key="month"
            class="month-calendar admin-month"
          >
            <div class="month-header">
              <h2 class="month-title">{{ props.year }}年{{ month }}月</h2>
            </div>
            <div class="calendar-grid">
            <div class="weekday-header">
              <div
                class="calendar-weekday"
                v-for="(wd, i) in weekdays"
                :key="wd"
                :class="{ sun: i === 0, sat: i === 6 }"
              >
                {{ wd }}
              </div>
            </div>
              <div
                class="calendar-week"
                v-for="(week, weekIndex) in generateWeeksForYearMonth(props.year, month)"
                :key="`${props.year}-${month}-${weekIndex}`"
              >
                <div
                  class="calendar-day"
                  v-for="day in week"
                  :key="day.date"
                  :class="getDayClasses(day)"
                  :title="getDayTitle(day)"
                  :aria-label="getDayTitle(day)"
                >
                  <div
                    class="day-number"
                    :class="{ sun: day.isSunday, sat: day.isSaturday }"
                  >
                    {{ day.dayNumber }}
                  </div>
                </div>
                <!-- 管理モードでは週カウンタを表示しない -->
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- 凡例（管理モードでは休日を強調） -->
    <div class="legend">
      <div class="legend-item">
        <div class="legend-indicator submitted" aria-hidden="true"></div>
        <span>達成</span>
      </div>
      <div class="legend-item">
        <div class="legend-indicator excused" aria-hidden="true"></div>
        <span>計画休</span>
      </div>
      <div class="legend-item">
        <div class="legend-indicator unexcused" aria-hidden="true"></div>
        <span>未実施</span>
      </div>
      <div class="legend-item">
        <div class="legend-indicator today" aria-hidden="true"></div>
        <span>今日</span>
      </div>
      <div class="legend-item">
        <div class="legend-indicator holiday" aria-hidden="true"></div>
        <span>休日</span>
      </div>
    </div>

    <!-- 休暇申請確認モーダル（ユーザーモードのみ） -->
    <div v-if="showExcuseModal && mode === 'user'" class="modal-overlay" @click="closeExcuseModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ modalText.title }}</h3>
        </div>
        <div class="modal-body">
          <p v-text="modalText.body"></p>
        </div>
        <div class="modal-actions">
          <button class="btn-cancel" @click="closeExcuseModal">キャンセル</button>
          <button class="btn-confirm" @click="confirmExcuseAction" :disabled="loading">
            {{ loading ? '処理中...' : modalText.confirm }}
          </button>
        </div>
      </div>
    </div>

    <!-- エラーモーダル -->
    <div v-if="showErrorModal" class="modal-overlay error-modal" @click="closeErrorModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>エラー</h3>
        </div>
        <div class="modal-body">
          <p>{{ errorMessage }}</p>
        </div>
        <div class="modal-actions">
          <button class="btn-confirm" @click="closeErrorModal">OK</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { supabase } from '../supabaseClient'
import LoadingModal from './common/LoadingModal.vue'
import { findHolidayBlocks, isRecordingLocked, getLockReasonMessage, isDateHoliday } from '../utils/recordingLock.js'
import { officialHolidays, ensureOfficialHolidays } from '../composables/officialHolidays.js'

// タイマーIDを保持（リーク防止）
let midnightTO = null
let midnightIV = null

// Props定義
const props = defineProps({
  mode: {
    type: String,
    default: 'user',
    validator: (value) => ['user', 'admin'].includes(value)
  },
  year: {
    type: Number,
    default: () => new Date().getFullYear()
  }
})

const router = useRouter()

// データ
const currentMonth = ref('')
const nextMonth = ref('')
const calendarData = ref({})
const ticketsByMonth = ref({}) // { '2025-10': {issued, used, remaining}, '2025-11': {...} }
const loading = ref(false)
const showExcuseModal = ref(false)
const showErrorModal = ref(false)
const selectedDay = ref(null)
const selectedAction = ref(null) // 'apply' | 'cancel'
const errorMessage = ref('')
// 休日データ（共有refを使用）

// 定数
const weekdays = ['日', '月', '火', '水', '木', '金', '土']

// 計算プロパティ
const currentMonthWeeks = computed(() => {
  return generateWeeks(currentMonth.value)
})

const nextMonthWeeks = computed(() => {
  return generateWeeks(nextMonth.value)
})

// 休日ブロック（連続する休日）の計算プロパティ
// ユーザーモードは「今月」「来月」の2ヶ月を対象に作成し、年末年始など年跨ぎ連休を統合
const holidayBlocks = computed(() => {
  if (props.mode === 'admin') {
    return findHolidayBlocks(officialHolidays.value, props.year)
  }
  // ユーザーモード：今月、来月の「年」を抽出（前月は不要）
  const months = [currentMonth.value, nextMonth.value]
  const years = [...new Set(months.map(m => Number(m.split('-')[0])))]
  
  // 年ごとに作成 → 一つにまとめて年跨ぎブロックをマージ
  const blocks = years.flatMap(y => findHolidayBlocks(officialHolidays.value, y))
  return mergeBlocksAcrossYears(blocks)
})

// 年を跨いで隣接しているブロックを結合（例：12/29-12/31 と 1/1-1/3）
const mergeBlocksAcrossYears = (blocks) => {
  if (!blocks || blocks.length === 0) return []
  const sorted = [...blocks].sort((a, b) => a[0].localeCompare(b[0]))
  const result = []
  for (const block of sorted) {
    if (result.length === 0) {
      result.push([...block])
      continue
    }
    const last = result[result.length - 1]
    const lastEnd = last[last.length - 1]
    const nextStart = block[0]
    if (ymdPlusOne(lastEnd) === nextStart) {
      // 連続していれば結合
      result[result.length - 1] = [...last, ...block]
    } else {
      result.push([...block])
    }
  }
  return result
}

// YYYY-MM-DD の翌日を返す（JST基準でOK：日付文字列はUTC日付扱いで+1日）
const ymdPlusOne = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + 1)
  const yy = dt.getUTCFullYear()
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(dt.getUTCDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

// 前月を取得する関数（不要になったため削除）

// モーダル文言の計算プロパティ（ユーザーモードのみ）
const modalText = computed(() => {
  if (!selectedDay.value || props.mode === 'admin') return { title: '', body: '', confirm: '' }
  const d = selectedDay.value.date
  const remaining = getRemainingFor(d)
  if (selectedAction.value === 'apply') {
    // 申請時：使用後をプレビュー（残 – 1）
    return {
      title: '休暇申請',
      body: `${formatDate(d)}を「計画休」にします。\n🎫1枚使います（${Math.max(remaining-1,0)}/4）。よろしいですか？`,
      confirm: '休みにする'
    }
  } else {
    // 取消時：返却後をプレビュー（残 + 1）
    return {
      title: '休暇取り消し',
      body: `${formatDate(d)}の「計画休」を取り消します。\n🎫1枚戻ります（${Math.min(remaining+1,4)}/4）。よろしいですか？`,
      confirm: '取り消す'
    }
  }
})

// 月ごとのチケット残数を取得（ユーザーモードのみ）
const getTicketsForMonth = (month) => {
  if (props.mode === 'admin') return 0
  const ticketData = ticketsByMonth.value[month]
  if (!ticketData) {
    // ローディング中は表示しない（コンソールログを削除）
    return 4 // データが存在しない場合のみデフォルト値
  }
  console.log(`🔍 チケット残数取得: ${month}`, ticketData)
  return ticketData.remaining || 4 // remainingがundefinedの場合はデフォルト値
}

// 日付から月別残数を取得（なければ仮値）
const getRemainingFor = (dateStr) => {
  const [y, m] = dateStr.split('-')
  const month = `${y}-${m}`
  return ticketsByMonth.value?.[month]?.remaining ?? 4
}

// メソッド
const ym = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`
const toYmd = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`

// 今日のYYYY-MM-DD（JST基準）
const todayJST = () => {
  // フロントで"JSTの今日（YYYY-MM-DD）"を得る
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })
}

// 後方互換性のため
const todayYmd = todayJST

const initializeMonths = () => {
  // JST基準で月を初期化
  const today = todayJST()
  const [year, month] = today.split('-').map(Number)
  
  currentMonth.value = `${year}-${String(month).padStart(2, '0')}`
  
  // 来月を計算（JST基準）
  const nextMonthDate = new Date(year, month, 1) // monthは1ベースなのでそのまま使用
  nextMonth.value = `${nextMonthDate.getFullYear()}-${String(nextMonthDate.getMonth() + 1).padStart(2, '0')}`
  
  console.log('今月:', currentMonth.value, '来月:', nextMonth.value)
}

const generateWeeks = (month) => {
  if (!month) return []
  
  const [y, m] = month.split('-').map(Number)
  const year = y
  const monthIndex = m - 1
  
  const firstDay = new Date(year, monthIndex, 1)
  
  // 日曜始まりに合わせて「その週の日曜」へ戻す
  const startDate = new Date(firstDay)
  const dow = firstDay.getDay() // 0=日…6=土
  const sundayOffset = -dow  // 日曜=0なので、そのまま戻す
  startDate.setDate(firstDay.getDate() + sundayOffset)
  
  const weeks = []
  const cur = new Date(startDate)
  
  while (true) {
    const week = []
    for (let i = 0; i < 7; i++) {
      const date = new Date(cur)
      // すべてJSTに正規化
      const ymd = date.toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' }) // YYYY-MM-DD
      const [yy, mm, dd] = ymd.split('-').map(Number)
      const isCurrentMonth = (mm === monthIndex + 1)         // 月は1ベース
      const dow = new Date(Date.UTC(yy, mm - 1, dd)).getUTCDay()
      const isSunday = dow === 0
      const isSaturday = dow === 6
      
      week.push({
        date: ymd,
        dayNumber: dd,
        isCurrentMonth,
        isSunday,
        isSaturday
      })
      
      cur.setDate(cur.getDate() + 1)
    }
    
    weeks.push(week)
    
    // 月をまたいだら終了（JST基準）
    const curJst = cur.toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })
    const curJstMonth = Number(curJst.slice(5, 7))
    if (curJstMonth !== monthIndex + 1) break
  }
  
  return weeks
}

// 管理モード用：指定年の指定月の週を生成
const generateWeeksForYearMonth = (year, month) => {
  const m = String(month).padStart(2, '0')
  return generateWeeks(`${year}-${m}`)
}

function submissionCountOf(dd) {
  if (!dd) return 0;
  return (
    dd.submitted_count ??
    dd.submission_count ??
    dd.submission_v3_count ??
    (Array.isArray(dd.submissions_v3) ? dd.submissions_v3.length : 0) ??
    (Array.isArray(dd.submissions)    ? dd.submissions.length    : 0) ??
    0
  );
}

function isSubmittedDay(dd) {
  return dd?.status === 'SUBMITTED'
      || dd?.has_submission === true
      || submissionCountOf(dd) > 0;
}

const getDayClasses = (day) => {
  const c = []
  if (!day.isCurrentMonth) c.push('other-month')
  
  // 休日判定（最優先）
  if (isDateHoliday(day.date, officialHolidays.value)) {
    c.push('holiday')     // 休日スタイルは付ける
  }                       // でも return しない（今日リングを後で重ねる）
  
  // 管理モードでは休日以外は通常表示
  if (props.mode === 'admin') {
    if (isToday(day.date)) c.push('today')
    return c
  }
  
  // ユーザーモード：通常の状態判定
  const dd = calendarData.value[day.date] || {};
  const isSubmitted = isSubmittedDay(dd);

  const isExcusedStatus = dd.status === 'EXCUSED'
    || dd.is_excused === true
    || isExcused(day.date);

  if (isSubmitted) c.push('submitted');
  else if (isExcusedStatus) c.push('excused');
  else if (dd.status === 'UNEXCUSED' || (isPast(day.date) && !isSubmitted && !isExcusedStatus)) {
    c.push('unexcused');
  }
  
  if (isToday(day.date)) c.push('today') // 最後に
  return c
}

// 日付のタイトル生成
const getDayTitle = (day) => {
  // 曜日は UTC 基準で安定算出（"sv-SE"で得たJST ymdをUTCに投影）
  const [yy, mm, ddNum] = day.date.split('-').map(Number)
  const dow = new Date(Date.UTC(yy, mm - 1, ddNum)).getUTCDay()
  const dayNames = ['日', '月', '火', '水', '木', '金', '土']
  const dayName = dayNames[dow]
  
  if (isDateHoliday(day.date, officialHolidays.value)) {
    return `${day.date} (${dayName}) - 休日`
  }

  if (props.mode === 'admin') {
    return `${day.date} (${dayName}) - 通常日`
  }

  // ユーザーモード：録音ロック情報を含むタイトル
  const dayData = calendarData.value[day.date] || {}
  const baseTitle = `${day.date} (${dayName}) ${dayData.status || 'PENDING'}`
  
  if (isRecordingLockedForDate(day.date)) {
    const lockMessage = getRecordingLockMessage(day.date)
    return `${baseTitle} - ${lockMessage}`
  }
  
  return baseTitle
}

// 日付判定関数（JST基準）
const isPast = (d) => d < todayJST()
const isToday = (d) => d === todayJST()
const isTodayOrFuture = (d) => d >= todayJST()

const isExcused = (date) => {
  const d = calendarData.value[date]
  return d?.status === 'EXCUSED' || d?.is_excused === true
}

// 休暇ボタンの活性/非活性（ユーザーモードのみ）
// 申請: 今日以降のみ
const canRequestExcuse = (dateStr) => props.mode === 'user' && isTodayOrFuture(dateStr)
// 取消: 今日以降のみ
const canCancelExcuse = (dateStr) => props.mode === 'user' && isTodayOrFuture(dateStr)

// ローカル状態を即座に更新（楽観更新）
const setLocalStatus = (date, status) => {
  const prev = calendarData.value[date] || { date, status: 'PENDING', submitted_count: null }
  // Vueのリアクティビティを確実にトリガー
  calendarData.value = {
    ...calendarData.value,
    [date]: { ...prev, status }
  }
}


const formatMonth = (month) => {
  if (!month) return ''
  const [year, monthNum] = month.split('-')
  return `${year}年${parseInt(monthNum)}月`
}

const formatDate = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number)
  return `${m}月${d}日`
}

const handleDayClick = (day) => {
  if (!day.isCurrentMonth) return
  
  // 管理モードではクリック無効
  if (props.mode === 'admin') return
  
  // 休日はクリック無効
  if (isDateHoliday(day.date, officialHolidays.value)) return
  
  // 🔍 デバッグ用ログ
  console.log('=== 日付クリックデバッグ ===')
  console.log('クリック日付:', day.date)
  console.log('今日の日付:', todayJST())
  console.log('isPast結果:', isPast(day.date))
  console.log('isToday結果:', isToday(day.date))
  console.log('isExcused結果:', isExcused(day.date))
  console.log('canRequestExcuse結果:', canRequestExcuse(day.date))
  console.log('canCancelExcuse結果:', canCancelExcuse(day.date))
  
  // 今日以降の日付のみ操作可能
  if (!isTodayOrFuture(day.date)) return
  
  selectedDay.value = day
  // ここで確定（楽観更新の前！）
  selectedAction.value = isExcused(day.date) ? 'cancel' : 'apply'
  
  // エラーモーダルが表示中でない場合のみ休暇モーダルを表示
  if (!showErrorModal.value) {
    showExcuseModal.value = true
  }
}

// 休日データを取得
const loadHolidayData = async () => {
  try {
    const { data, error } = await supabase
      .from('official_holidays')
      .select('date')
      .order('date', { ascending: true })
    
    if (error) throw error
    
    // Set化して高速判定を実現
    officialHolidays.value = new Set((data || []).map(h => h.date))
    console.log('休日データ取得成功:', officialHolidays.value.size, '件')
  } catch (error) {
    console.error('休日データの取得エラー:', error)
    officialHolidays.value = new Set()
  }
}


// 録音ロック判定関数
const isRecordingLockedForDate = (date) => {
  if (props.mode === 'admin') return false // 管理モードではロック判定なし
  
  const submittedDates = Object.keys(calendarData.value).filter(d => {
    const dayData = calendarData.value[d]
    return dayData?.status === 'SUBMITTED' || (dayData?.submitted_count ?? 0) > 0
  })
  
  return isRecordingLocked(date, submittedDates, holidayBlocks.value)
}

// 録音ロック理由メッセージ取得
const getRecordingLockMessage = (date) => {
  if (props.mode === 'admin') return ''
  return getLockReasonMessage(date, holidayBlocks.value)
}

// 共通: 期待通り JSON なら parse、HTML等は先頭を拾ってわかりやすく投げる
async function fetchJSON(url, options = {}) {
  const res = await fetch(url, {
    redirect: 'follow',
    cache: 'no-store',
    ...options,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });

  const ct = res.headers.get('content-type') || '';
  const text = await res.text();

  let data = null;
  if (ct.includes('application/json')) {
    try { data = JSON.parse(text); } catch {}
  }
  if (!res.ok) {
    const head = text.slice(0, 200);
    const hint = ct.includes('text/html') ? '（HTML応答）' : '';
    throw new Error(`${res.status} ${res.statusText} ${hint} ${head}`);
  }
  if (!data) {
    const head = text.slice(0, 200);
    throw new Error(`Unexpected response (Content-Type: ${ct}). ${head}`);
  }
  return data;
}

// 404 の時だけ"候補エンドポイント"に自動フォールバック
const EXCUSE_ENDPOINTS = [
  '/api/excused',              // ← バックエンドの実際のエンドポイント
  '/api/calendar/excuse',      // ← 旧エンドポイント（フォールバック用）
  '/api/calendar/excuse/',
  '/api/calendar/excuses',     // ←多い
  '/calendar/excuse',          // ←環境によっては /api 無し
];

async function callExcuseAPI(method, body) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('認証が必要です');

  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com';
  let lastErr = null;

  for (const path of EXCUSE_ENDPOINTS) {
    const url = `${apiBaseUrl}${path}`;
    try {
      console.log('⏳ trying excuse endpoint:', url);
      console.log('🔍 リクエスト詳細:', { method, url, body });
      const result = await fetchJSON(url, {
        method,
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(body),
      });
      console.log('✅ 成功:', result);
      return result;
    } catch (e) {
      lastErr = e;
      console.error('❌ エラー:', e.message);
      // 404 のときだけ次候補へ。401/403/500は即座に中断した方が原因特定しやすい
      if (!String(e?.message || '').startsWith('404')) throw e;
      console.warn('⚠️ 404 Not Found, trying next endpoint:', url);
    }
  }
  // 全滅
  throw new Error(`休暇申請APIのURLが見つかりません。最後のエラー: ${lastErr?.message || lastErr}`);
}

// 置き換え
const requestExcuse = (date) => callExcuseAPI('POST',  { date });
const cancelExcuse  = (date) => callExcuseAPI('DELETE', { date });

const loadCalendarData = async () => {
  // 管理モードではカレンダーデータの読み込みをスキップ
  if (props.mode === 'admin') {
    loading.value = false
    return
  }
  
  try {
    loading.value = true
    
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      router.push('/login')
      return
    }
    
    // 月を切り替える前に一旦リセット（前月の残骸を防ぐ）
    calendarData.value = {}
    ticketsByMonth.value = {}
    
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'
    
    // 当月と次月のデータのみ取得（前月は不要）
    const response = await fetch(`${apiBaseUrl}/api/calendar?months=${currentMonth.value},${nextMonth.value}`, {
      headers: {
        'Authorization': `Bearer ${session.access_token}`
      }
    })
    
    const text = await response.text()
    console.log('calendar status=', response.status, 'head=', text.slice(0,120))
    
    // 🔍 レスポンスがHTMLかどうかをチェック
    if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
      console.error('❌ APIがHTMLを返しています（404または認証エラーの可能性）')
      console.error('レスポンス内容:', text.slice(0, 500))
      throw new Error('APIエンドポイントが存在しないか、認証に失敗しています')
    }
    
    let result
    try {
      result = JSON.parse(text)
    } catch (parseError) {
      console.error('❌ JSONパースエラー:', parseError)
      console.error('レスポンス内容:', text.slice(0, 500))
      throw new Error('APIレスポンスがJSONではありません')
    }
    
    // 🔍 APIレスポンスの全体構造を確認
    console.log('=== APIレスポンス全体 ===')
    console.log('result:', result)
    console.log('result.data:', result.data)
    console.log('result.data.days:', result.data?.days)
    console.log('result.data.daysの型:', typeof result.data?.days)
    console.log('result.data.daysの長さ:', result.data?.days?.length)
    
    if (result.success) {
      // チケット情報を設定
      ticketsByMonth.value = result.data.tickets_by_month || {}
      
      // 先にカレンダーデータを反映（ログより前に！）
      if (result.data.days && Array.isArray(result.data.days)) {
        result.data.days.forEach(day => {
          calendarData.value[day.date] = day
        })
        console.log('✅ カレンダーデータを反映完了:', result.data.days.length, '件')
      } else {
        console.warn('⚠️ result.data.daysが存在しないか配列ではありません:', result.data.days)
      }
      
      console.log('カレンダーデータ取得成功:', {
        ticketsByMonth: ticketsByMonth.value,
        daysCount: result.data.days?.length || 0
      })
      
      // 🔍 データ内容の詳細確認
      console.log('=== カレンダーデータ詳細 ===')
      console.log('APIレスポンスのdays:', result.data.days)
      console.log('calendarData.value:', calendarData.value)
      
      // 最初の数日分のデータを確認
      const sampleDays = result.data.days?.slice(0, 5) || []
      console.log('サンプル日付データ:', sampleDays)
      
      // 🔍 詳細調査用ログ（反映後）
      console.log('=== 詳細調査用ログ ===')
      console.log('tickets_by_month (APIレスポンス):', result.data.tickets_by_month)
      console.log('ticketsByMonth (Vue状態):', ticketsByMonth.value)
      
      // 当月と次月のチケット使用数のみ表示
      const currentMonthKey = currentMonth.value
      const nextMonthKey = nextMonth.value
      console.log(`${currentMonthKey}のチケット使用数:`, ticketsByMonth.value[currentMonthKey]?.used || 0)
      console.log(`${nextMonthKey}のチケット使用数:`, ticketsByMonth.value[nextMonthKey]?.used || 0)
    } else {
      throw new Error(result.error || 'カレンダーデータの取得に失敗しました')
    }
  } catch (error) {
    console.error('カレンダーデータ取得エラー:', error)
    errorMessage.value = 'カレンダーデータの取得に失敗しました'
    showErrorModal.value = true
  } finally {
    loading.value = false
  }
}

const confirmExcuseAction = async () => {
  if (!selectedDay.value || !selectedAction.value) return
  
  // 重複クリック防止
  if (loading.value) return
  
  loading.value = true
  const date = selectedDay.value.date
  const prevStatus = calendarData.value[date]?.status ?? 'PENDING'
  const action = selectedAction.value // ← 固定参照
  
  try {
    if (action === 'cancel') {
      console.log('=== 休暇取り消し開始 ===')
      console.log('取り消し前の状態:', {
        date,
        prevStatus,
        ticketsByMonth: ticketsByMonth.value
      })
      
      setLocalStatus(date, 'PENDING')       // 楽観更新
      const res = await cancelExcuse(date)  // API
      
      console.log('APIレスポンス:', res)
      
      // 月の残数を同期（リアクティブ更新）
      if (res?.data?.month) {
        const m = res.data.month
        ticketsByMonth.value = { 
          ...ticketsByMonth.value, 
          [m]: { 
            ...(ticketsByMonth.value[m] || {issued:4, used:0, remaining:4}), 
            remaining: res.data.remaining_tickets 
          }
        }
        console.log('取り消し後の状態:', {
          month: m,
          remaining: res.data.remaining_tickets,
          ticketsByMonth: ticketsByMonth.value
        })
      }
    } else {
      console.log('=== 休暇申請開始 ===')
      console.log('申請前の状態:', {
        date,
        prevStatus,
        ticketsByMonth: ticketsByMonth.value
      })
      
      setLocalStatus(date, 'EXCUSED')       // 楽観更新
      const res = await requestExcuse(date) // API
      
      console.log('APIレスポンス:', res)
      
      if (res?.data?.month) {
        const m = res.data.month
        ticketsByMonth.value = { 
          ...ticketsByMonth.value, 
          [m]: { 
            ...(ticketsByMonth.value[m] || {issued:4, used:0, remaining:4}), 
            remaining: res.data.remaining_tickets 
          }
        }
        console.log('申請後の状態:', {
          month: m,
          remaining: res.data.remaining_tickets,
          ticketsByMonth: ticketsByMonth.value
        })
      }
    }
    closeExcuseModal()
  } catch (e) {
    console.error('休暇操作エラー:', e)
    setLocalStatus(date, prevStatus)        // ロールバック
    
    // エラーメッセージをより具体的に
    let errorMsg = '操作に失敗しました'
    if (e.message) {
      if (e.message.includes('使い切っています')) {
        errorMsg = '今月のチケットは使い切っています'
      } else if (e.message.includes('提出済み')) {
        errorMsg = e.message
      } else {
        errorMsg = e.message
      }
    }
    
    errorMessage.value = errorMsg
    showErrorModal.value = true
  } finally {
    loading.value = false
  }
}

const closeExcuseModal = () => {
  showExcuseModal.value = false
  selectedDay.value = null
  selectedAction.value = null
}

const closeErrorModal = () => {
  showErrorModal.value = false
  errorMessage.value = ''
  // エラーモーダルを閉じる際に、選択状態もクリア
  selectedAction.value = null
  selectedDay.value = null
}

// 月替わり自動切替のスケジューリング（ユーザーモードのみ）
const scheduleMidnightReload = () => {
  if (props.mode === 'admin') return
  
  const now = new Date()
  const jstNow = new Date(now.toLocaleString('en-US', {timeZone: 'Asia/Tokyo'}))
  const jstNext = new Date(jstNow)
  jstNext.setDate(jstNow.getDate() + 1)
  jstNext.setHours(0, 0, 0, 0)
  const ms = jstNext - jstNow
  
  midnightTO = setTimeout(() => {
    console.log('月替わり検出: カレンダーを再読み込み')
    initializeMonths()               // 月だけ更新（watch が拾う）
    
    // 24時間ごとに再実行
    midnightIV = setInterval(() => {
      console.log('定期月替わりチェック: カレンダーを再読み込み')
      initializeMonths()
    }, 24 * 60 * 60 * 1000)
  }, ms)
}

// 画面復帰時の再同期（ユーザーモードのみ）
const handleVisibilityChange = async () => {
  if (!document.hidden && props.mode === 'user') {
    console.log('画面復帰: カレンダーを再同期')
    try {
      // Phase 1: 初期化
      initializeMonths()
      
      // Phase 2: 並列処理
      const [holidayResult] = await Promise.all([
        ensureOfficialHolidays()  // 休日データ再取得
      ])
      
      // Phase 3: 依存処理
      await loadCalendarData()  // カレンダーデータ再取得
      
      console.log('✅ 画面復帰時の再同期完了')
    } catch (error) {
      console.error('❌ 画面復帰時の再同期エラー:', error)
    }
  }
}

// ライフサイクル
onMounted(async () => {
  try {
    // Phase 1: 初期化（同期処理）
    initializeMonths()
    
    // Phase 2: 並列処理（独立したデータ取得）
    const [holidayResult] = await Promise.all([
      ensureOfficialHolidays()  // 休日データ取得
    ])
    
    // Phase 3: 依存処理（Phase 2の完了後に実行）
    await loadCalendarData()  // カレンダーデータ取得（休日データに依存）
    
    // Phase 4: 後処理（非同期）
    scheduleMidnightReload()
    
    // 画面復帰時の再同期を設定
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    console.log('✅ カレンダー初期化完了')
  } catch (error) {
    console.error('❌ カレンダー初期化エラー:', error)
    loading.value = false
  }
})

// クリーンアップ
onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  if (midnightTO) clearTimeout(midnightTO)
  if (midnightIV) clearInterval(midnightIV)
})

// 月変更の監視（休日データは再取得不要）
watch([currentMonth, nextMonth], async () => {
  try {
    console.log('月変更検知: カレンダーデータを再取得')
    await loadCalendarData()
    console.log('✅ 月変更時の再取得完了')
  } catch (error) {
    console.error('❌ 月変更時の再取得エラー:', error)
  }
})
</script>

<style scoped>
.calendar-page {
  min-height: 100vh;
  background: #f8fafc;
  padding: 1rem;
  padding-bottom: calc(2.5rem + env(safe-area-inset-bottom)); /* 余白を少し増やす */
}

/* 管理モード用スタイル */
.calendar-page.admin-mode {
  background: white;
  padding: 0;
}

.calendar-page.admin-mode .calendar-container {
  background: white;
  border-radius: 0;
  box-shadow: none;
}

.admin-header {
  text-align: center;
  margin-bottom: 2rem;
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.admin-header h2 {
  color: #1f2937;
  margin-bottom: 0.5rem;
  font-size: 1.8rem;
}

.admin-subtitle {
  color: #6b7280;
  margin: 0;
  font-size: 1rem;
}

.calendar-header {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.header-stats {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.ticket-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f3f4f6;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  color: #374151;
}

.ticket-icon {
  font-size: 1.2rem;
}


.header-subtitle {
  text-align: center;
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0;
}

.calendar-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.month-calendar {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.month-calendar:last-child {
  margin-bottom: 0;
}

.month-header {
  text-align: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.month-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.calendar-grid {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.weekday-header {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr)) !important;
  gap: 0.5rem !important;
  margin-bottom: 0.5rem;
}

.calendar-weekday {
  text-align: center;
  font-weight: 600;
  color: #6b7280;
  padding: 0.5rem;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.5rem;
}

.calendar-weekday.sat {
  color: #3b82f6;
}

.calendar-weekday.sun {
  color: #dc2626;
}


.calendar-week {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr)) !important;
  gap: 0.5rem !important;
}

.calendar-day {
  aspect-ratio: 1;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
  background: white;
}

.calendar-day:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.calendar-day.other-month {
  opacity: 0.35;
  cursor: default;
}

.calendar-day.other-month:hover {
  background: white;
  border-color: #e5e7eb;
}

/* 状態色（背景と枠だけ） */
.calendar-day.submitted { 
  background: #dcfce7; 
  border-color: #22c55e; 
}

.calendar-day.excused { 
  background: #dbeafe; 
  border-color: #3b82f6; 
}

.calendar-day.unexcused { 
  background: #f3f4f6; 
  border-color: #9ca3af; 
}

/* 休日を「斜線ストライプ＋赤系ボーダー＋角チップ」で強調 */
.calendar-day.holiday {
  /* 提出/計画休より強く見せたいので背景は模様つき */
  background: repeating-linear-gradient(
    -45deg,
    #fff1f2 0 10px,   /* very light red */
    #ffe4e6 10px 20px /* light red */
  );
  border-color: #fb7185; /* rose-400 */
  opacity: 1;            /* ← 既存の0.7をやめる */
  cursor: default;
  position: relative;    /* 角チップの基点 */
  box-shadow: inset 0 0 0 2px rgba(251, 113, 133, 0.35); /* ほんのり縁取り */
}

/* hoverで色が飛ばないように固定 */
.calendar-day.holiday:hover {
  background: repeating-linear-gradient(-45deg, #fff1f2 0 10px, #ffe4e6 10px 20px);
  border-color: #fb7185;
}

/* 休日は曜日に関係なく日付数字を赤＆太字で */
.calendar-day.holiday .day-number {
  color: #b91c1c !important; /* red-700 */
  font-weight: 700;
}


/* 今日が休日の場合も今日リングが見えるように重ね順＆二重縁取りを微調整 */
.calendar-day.holiday.today {
  box-shadow:
    inset 0 0 0 2px #f59e0b,   /* today ring */
    inset 0 0 0 4px #fed7aa;   /* today ring 外側を淡く */
}

/* 管理モードでは休日以外のクリックも無効 */
.calendar-page.admin-mode .calendar-day:not(.holiday) {
  cursor: default;
}

.calendar-page.admin-mode .calendar-day:not(.holiday):hover {
  background: white;
  border-color: #e5e7eb;
}

/* 管理モード用：年間カレンダー */
.yearly-calendar {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.admin-month {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  margin-bottom: 1rem;
}

.admin-month .month-header {
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.admin-month .month-title {
  font-size: 1.2rem;
  color: #1f2937;
  margin: 0;
}


/* 管理モード用：レスポンシブ */
@media (max-width: 1200px) {
  .yearly-calendar {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1rem;
  }
}

@media (max-width: 768px) {
  .yearly-calendar {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  
  .admin-month {
    padding: 0.75rem;
  }
  
  .admin-month .month-title {
    font-size: 1.1rem;
  }
  
  /* モバイルでもチケット表示を横一列に */
  .header-stats {
    flex-wrap: nowrap;
    gap: 1rem;
  }
  
  .ticket-info {
    flex: 1;
    min-width: 0;
  }
  
  .ticket-text {
    font-size: 0.9rem;
  }
}

/* 今日：枠のみ強調（他の色に重ねても崩れない） */
.calendar-day.today { 
  box-shadow: 0 0 0 2px #f59e0b inset; 
}

.day-number {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.day-number.sun {
  color: #dc2626;
}

.day-number.sat {
  color: #3b82f6;
}


/* 凡例 */
.legend {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  background: white;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  overflow: visible;
  min-height: 1.6rem;
  padding: 0.25rem 0;
}

.legend-indicator {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 2px solid;
  flex-shrink: 0;
}

.legend-indicator.submitted { 
  background: #dcfce7; 
  border-color: #22c55e; 
}

.legend-indicator.excused { 
  background: #dbeafe; 
  border-color: #3b82f6; 
}

.legend-indicator.unexcused { 
  background: #f3f4f6; 
  border-color: #9ca3af; 
}

.legend-indicator.today { 
  background: #fff7ed; 
  border-color: #f59e0b; 
}

.legend-indicator.holiday {
  background: repeating-linear-gradient(
    -45deg,
    #fff1f2 0 6px,
    #ffe4e6 6px 12px
  );
  border-color: #ef4444;
}

.legend-item span {
  color: #1e293b;
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
  text-shadow: 0 0 1px rgba(255,255,255,0.8);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

/* コントラスト確保（WCAG対応） */
@media (prefers-contrast: more) {
  .legend-indicator.submitted { border-color:#15803d; }
  .legend-indicator.excused   { border-color:#1d4ed8; }
  .legend-indicator.unexcused { border-color:#6b7280; }
  .legend-indicator.today     { border-color:#b45309; }
  .legend-indicator.holiday   { border-color:#64748b; }
}

/* コントラスト強調モード（見やすさ最優先） */
@media (prefers-contrast: more) {
  .calendar-day.holiday {
    border-color: #ef4444;
    box-shadow: inset 0 0 0 2px #ef4444;
    background: #ffe4e6; /* 模様より無地の方が視認しやすいケース */
  }
  .legend-indicator.holiday {
    background: #ffe4e6;
    border-color: #ef4444;
  }
}

/* Android向けフォント補正 */
@media screen and (-webkit-min-device-pixel-ratio: 1) {
  .legend-item span {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }
}

/* モーダル */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.modal-header {
  margin-bottom: 1rem;
}

.modal-header h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.modal-body {
  margin-bottom: 1.5rem;
}

.modal-body p {
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
}

.modal-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

.btn-cancel, .btn-confirm {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-cancel {
  background: #f3f4f6;
  color: #6b7280;
}

.btn-cancel:hover {
  background: #e5e7eb;
}

.btn-confirm {
  background: #3b82f6;
  color: white;
}

.btn-confirm:hover:not(:disabled) {
  background: #2563eb;
}

.btn-confirm:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

/* レスポンシブ */
@media (max-width: 480px) {
  .calendar-page {
    padding: 0.75rem;
  }
  
  .calendar-header {
    padding: 1rem;
  }
  
  .month-calendar {
    padding: 0.75rem;
  }
  
  .calendar-day {
    font-size: 0.75rem;
  }
  
  .day-number {
    font-size: 0.75rem;
  }
  
  
  .legend {
    gap: 1rem;
    padding: 0.75rem 1rem;
  }
  
  .legend-item span {
    font-size: 0.8rem;
  }
  
  .legend-indicator {
    width: 18px;
    height: 18px;
  }
}

@media (max-width: 400px) {
  .legend-item span {
    font-size: 0.75rem;
  }
  
  .legend-indicator {
    width: 16px;
    height: 16px;
  }
}
</style>
